// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { NormaliseRules } from '../types';

/**
 * Argument normalisation is where the trajectory aligner lives or dies, and the two failure
 * directions are NOT symmetric — which is the entire reason the defaults lean the way they do.
 *
 *   Under-normalising fails LOUDLY. Every run has a fresh request id, so every step differs, the
 *   alignment degenerates to all-gaps, and the user is told 300 things broke. They disbelieve it
 *   immediately and tighten the rules.
 *
 *   Over-normalising fails SILENTLY. A rule that masks too much makes two genuinely different
 *   calls look identical, the report reads "0 regressions", and the bug ships.
 *
 * So every default is the conservative choice, and the two rules that can hide a real bug with no
 * compensating benefit (`sortArrays`, `maskLongHex`) are OFF. The panel that shows the resulting
 * symbol alphabet and its collision count is not a nicety — it is the only way a user can see
 * over-normalisation happening.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO8601 = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;
const PREFIXED_ID = /^(req|trace|span|evt|run|call|msg|tool)_[A-Za-z0-9]{6,}$/;
const LONG_HEX = /^[0-9a-f]{16,}$/i;

/**
 * Epoch-millisecond masking is RANGE-GUARDED. Without the guard, a rule intended for timestamps
 * clobbers every large integer argument the model passes — an order total in cents, a row count, a
 * byte size — and silently hides a change in any of them. 1e12–2e12 is roughly 2001 to 2033.
 */
const EPOCH_MS_MIN = 1e12;
const EPOCH_MS_MAX = 2e12;

export interface NormaliseStats {
  /** Keys removed by `dropKeys`, so the UI can say what was thrown away. */
  readonly droppedKeys: readonly string[];
}

export function normaliseArgs(
  args: unknown,
  rules: NormaliseRules,
  stats?: { droppedKeys: Set<string> },
): unknown {
  const walk = (v: unknown): unknown => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number') {
      if (rules.maskEpochMs && Number.isInteger(v) && v >= EPOCH_MS_MIN && v <= EPOCH_MS_MAX) {
        return '<epoch-ms>';
      }
      if (rules.roundFloat && !Number.isInteger(v)) {
        return Number(v.toFixed(rules.floatDigits));
      }
      return v;
    }
    if (typeof v === 'string') {
      if (rules.maskUuid && UUID.test(v)) return '<uuid>';
      if (rules.maskIso8601 && ISO8601.test(v)) return '<timestamp>';
      if (rules.maskPrefixedId && PREFIXED_ID.test(v)) return '<id>';
      if (rules.maskLongHex && LONG_HEX.test(v)) return '<hex>';
      return v;
    }
    if (typeof v !== 'object') return v;
    if (Array.isArray(v)) {
      const mapped = v.map(walk);
      if (rules.sortArrays) {
        return [...mapped].sort((x, y) => (JSON.stringify(x) < JSON.stringify(y) ? -1 : 1));
      }
      return mapped;
    }
    const src = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    const keys = rules.sortObjectKeys ? Object.keys(src).sort() : Object.keys(src);
    for (const k of keys) {
      if (rules.dropKeys.includes(k)) {
        stats?.droppedKeys.add(k);
        continue;
      }
      out[k] = walk(src[k]);
    }
    return out;
  };
  return walk(args);
}

/**
 * Keys a user should think twice about adding to `dropKeys`.
 *
 * In the measured prototype, adding `priority` silently hid a `high -> low` downgrade. Anything
 * that looks like it carries meaning rather than plumbing gets a warning next to it in the UI.
 */
export const SUSPICIOUS_DROP_KEYS = new Set([
  'priority',
  'status',
  'amount',
  'total',
  'quantity',
  'user',
  'user_id',
  'customer',
  'customer_id',
  'email',
  'query',
  'reason',
  'severity',
  'level',
  'category',
]);
